import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import api from '../../services/api';

const orderedDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Returns the Monday (week_start) of the week containing the given date
const getMondayOf = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Format a Date to 'YYYY-MM-DD' (used as the week_start key for the API)
const toISODate = (date) => date.toISOString().split('T')[0];

// Format a Date to 'MM/DD' for display
const toDisplayDate = (date) =>
  date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });

export const EmployeeDashboard = ({ dashboardData, user }) => {
  // ─── Emoji state ────────────────────────────────────────────────────────────
  const [avatarEmoji, setAvatarEmoji] = useState('🙂');
  const [emojiDialogOpen, setEmojiDialogOpen] = useState(false);
  const [emojiOptions, setEmojiOptions] = useState([]);
  const [isLoadingEmojis, setIsLoadingEmojis] = useState(false);
  const [emojiError, setEmojiError] = useState('');

  // ─── Schedule week navigation state ─────────────────────────────────────────
  // weekOffset: 0 = current week, 1 = next week, -1 = last week, etc.
  const [weekOffset, setWeekOffset] = useState(0);
  const [scheduleData, setScheduleData] = useState(null); // schedule for the viewed week
  const [scheduleReleased, setScheduleReleased] = useState(true); // admin liberou essa semana?
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  // The Monday of the current real week (never changes)
  const thisWeekMonday = getMondayOf(new Date());

  // The Monday of the week being viewed
  const viewedMonday = new Date(thisWeekMonday);
  viewedMonday.setDate(thisWeekMonday.getDate() + weekOffset * 7);

  const viewedSunday = new Date(viewedMonday);
  viewedSunday.setDate(viewedMonday.getDate() + 6);

  const isCurrentWeek = weekOffset === 0;

  // ─── Sync emoji from dashboardData ──────────────────────────────────────────
  useEffect(() => {
    if (!dashboardData) return;
    setAvatarEmoji(dashboardData?.avatar_emoji || '🙂');
  }, [dashboardData]);

  // ─── Sync schedule: current week comes from dashboardData prop ──────────────
  useEffect(() => {
    if (isCurrentWeek) {
      setScheduleData(dashboardData?.schedule || null);
      setScheduleReleased(dashboardData?.schedule_released !== false);
      setScheduleError('');
    }
  }, [isCurrentWeek, dashboardData]);

  // ─── Fetch schedule for non-current weeks ───────────────────────────────────
  const fetchWeekSchedule = useCallback(async (monday) => {
    setIsLoadingSchedule(true);
    setScheduleError('');
    try {
      const weekStart = toISODate(monday);
      const res = await api.get(`/schedule/me?week_start=${weekStart}`);
      // Expected response: { schedule: { MONDAY: '...', ... }, schedule_released: bool }
      setScheduleData(res.data?.schedule || {});
      setScheduleReleased(res.data?.schedule_released !== false);
    } catch (e) {
      setScheduleError('Could not load schedule for this week.');
      setScheduleData({});
    } finally {
      setIsLoadingSchedule(false);
    }
  }, []);

  useEffect(() => {
    if (!isCurrentWeek && user) {
      fetchWeekSchedule(viewedMonday);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, user]);

  // ─── Emoji handlers ──────────────────────────────────────────────────────────
  const fetchAllowedEmojis = useCallback(async () => {
    if (isLoadingEmojis) return;
    setIsLoadingEmojis(true);
    setEmojiError('');
    try {
      const res = await api.get('/emojis');
      const list = Array.isArray(res.data?.emojis) ? res.data.emojis : [];
      setEmojiOptions(list);
    } catch (e) {
      setEmojiError('Could not load emojis.');
    } finally {
      setIsLoadingEmojis(false);
    }
  }, [isLoadingEmojis]);

  const openEmojiPicker = useCallback(async () => {
    if (!emojiOptions.length) {
      await fetchAllowedEmojis();
    }
    setEmojiDialogOpen(true);
  }, [emojiOptions.length, fetchAllowedEmojis]);

  const saveEmoji = useCallback(
    async (emoji) => {
      const prev = avatarEmoji;
      setAvatarEmoji(emoji);
      setEmojiError('');
      try {
        const res = await api.put('/employees/me/emoji', { emoji });
        const saved = res.data?.emoji || emoji;
        setAvatarEmoji(saved);
        setEmojiDialogOpen(false);
      } catch (e) {
        setAvatarEmoji(prev);
        setEmojiError('Could not save emoji.');
      }
    },
    [avatarEmoji]
  );

  // ─── Early return after all hooks ────────────────────────────────────────────
  if (!user || !dashboardData) {
    return (
      <Typography sx={{ textAlign: 'center', mt: 5 }}>
        Error loading dashboard data or no data available.
      </Typography>
    );
  }

  const todayFormattedDay = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();

  const isCurrentlyWorking = () => {
    const clockStr = dashboardData?.clock || '--:-- | --:--';
    const [clockIn, clockOut] = clockStr.split(' | ');
    return clockIn !== '--:--' && clockOut === '--:--';
  };

  const shouldBlurFinancials = isCurrentlyWorking();

  const salaryPerHour = (() => {
    const totalDay = parseFloat(dashboardData?.total_day || 0);
    const clockStr = dashboardData?.clock || '--:-- | --:--';
    const [clockIn, clockOut] = clockStr.split(' | ');
    if (clockIn === '--:--' || clockOut === '--:--') return 0;
    try {
      const [inHour, inMin] = clockIn.split(':').map(Number);
      const [outHour, outMin] = clockOut.split(':').map(Number);
      let workedMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
      if (workedMinutes < 0) workedMinutes += 24 * 60;
      const workedHours = workedMinutes / 60;
      if (workedHours <= 0) return 0;
      return totalDay / workedHours;
    } catch {
      return 0;
    }
  })();

  const cardIcons = [
    { label: 'Name', value: dashboardData?.name, icon: '👤', shouldBlur: false },
    { label: 'Date', value: dashboardData?.date, icon: '📅', shouldBlur: false },
    { label: 'Resort', value: dashboardData?.resort, icon: '🏨', shouldBlur: false },
    { label: 'Total Sales', value: `$${dashboardData?.total_sales}`, icon: '🛒', shouldBlur: true },
    { label: 'Clock', value: dashboardData?.clock, icon: '⏰', shouldBlur: false },
    { label: 'Hourly', value: `$${dashboardData?.hourly_total || '0.00'}`, icon: '⌛', shouldBlur: false },
    { label: 'Commission', value: `$${dashboardData?.commission || '0.00'}`, icon: '🎟️', shouldBlur: false },
    { label: 'Tip', value: `$${dashboardData?.tip || '0.00'}`, icon: '💸', shouldBlur: true },
    { label: 'Bonus', value: `$${dashboardData?.charm_bonus || '0.00'}`, icon: '🪙', shouldBlur: false },
    { label: 'Total of Day', value: `$${dashboardData?.total_day || '0.00'}`, icon: '🧾', shouldBlur: false },
    { label: 'Week Total', value: `$${dashboardData?.total_week || '0.00'}`, icon: '👛', shouldBlur: false },
    { label: 'Group', value: dashboardData?.group || 'N/A', icon: '👥', shouldBlur: false },
    {
      // Só a %, sem o valor vendido — por isso não é borrado durante o turno.
      label: 'Daily Goal',
      value:
        dashboardData?.daily_goal_percent === null || dashboardData?.daily_goal_percent === undefined
          ? '--'
          : `${dashboardData.daily_goal_percent}%`,
      icon: '🎯',
      shouldBlur: false,
    },
    {
      label: 'Salary / Hour',
      value: salaryPerHour > 0 ? `$${salaryPerHour.toFixed(2)}` : '$0.00',
      icon: '📈',
      shouldBlur: false,
    },
  ];

  // The schedule to render (either from prop or fetched)
  const activeSchedule = isCurrentWeek ? dashboardData?.schedule : scheduleData;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mt: 1 }}>
      <Box sx={{ width: '100%', maxWidth: '650px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>

        {/* ── Emoji avatar ──────────────────────────────────────────────────── */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0.5 }}>
          <Tooltip title="Click to choose your emoji">
            <Box
              onClick={openEmojiPicker}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openEmojiPicker(); }}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            >
              <Box>
                <Box sx={{ fontSize: 72, lineHeight: 1, transform: 'translateY(2px)', position: 'relative', zIndex: 1 }}>
                  {avatarEmoji || '🙂'}
                </Box>
              </Box>
              <Box sx={{ mt: 0.75, width: 100, height: 4, borderRadius: 999, background: 'linear-gradient(90deg, #ec4899, #a855f7)' }} />
            </Box>
          </Tooltip>
        </Box>

        {emojiError ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 0.25 }}>
            <Typography variant="caption" sx={{ color: '#b91c1c', lineHeight: 1 }}>{emojiError}</Typography>
          </Box>
        ) : null}

        {/* ── Emoji picker dialog ───────────────────────────────────────────── */}
        <Dialog
          open={emojiDialogOpen}
          onClose={() => setEmojiDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(180deg, #ec4899, #a855f7)`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              backgroundColor: '#fff',
              border: '1px solid transparent',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25 }}>
            <Typography sx={{ fontWeight: 900 }}>Choose your emoji</Typography>
            <IconButton onClick={() => setEmojiDialogOpen(false)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pb: 2 }}>
            {isLoadingEmojis ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(6, 1fr)', sm: 'repeat(8, 1fr)' }, gap: 1, py: 1 }}>
                {emojiOptions.map((opt) => {
                  const em = opt?.emoji;
                  const label = opt?.label || '';
                  return (
                    <Tooltip key={em} title={label || 'Choose'} placement="top">
                      <Box
                        onClick={() => saveEmoji(em)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') saveEmoji(em); }}
                        sx={{
                          height: 46, borderRadius: 2, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 24, cursor: 'pointer', userSelect: 'none',
                          background: em === avatarEmoji ? 'rgba(168, 85, 247, 0.10)' : 'rgba(0,0,0,0.03)',
                          border: em === avatarEmoji ? '1px solid rgba(168, 85, 247, 0.35)' : '1px solid rgba(0,0,0,0.06)',
                        }}
                      >
                        {em}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
            {!isLoadingEmojis && !emojiOptions.length ? (
              <Typography sx={{ textAlign: 'center', opacity: 0.7, py: 2 }}>No emojis available.</Typography>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* ── Cards ─────────────────────────────────────────────────────────── */}
        <Grid container spacing={1} justifyContent="center">
          {cardIcons.map((item, idx) => (
            <Grid item xs={6} sm={6} key={idx} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                sx={{
                  minWidth: 170, maxWidth: 200, width: '100%', flex: 1, borderRadius: 2,
                  backgroundColor: '#fff', position: 'relative', overflow: 'hidden',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)', borderLeft: '3px solid transparent',
                  backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(180deg, #ec4899, #a855f7)`,
                  backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
                  p: 0.75, px: 1, transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                }}
              >
                <CardContent sx={{ height: 20, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 0, gap: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, transition: 'color 0.2s' }}>
                    <Box sx={{ fontSize: 16, lineHeight: 1 }}>{item.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.label}</Typography>
                  </Box>
                  <Typography
                    variant="h7"
                    sx={{
                      mb: -3, fontWeight: 'normal',
                      filter: (shouldBlurFinancials && item.shouldBlur) ? 'blur(6px)' : 'none',
                      userSelect: (shouldBlurFinancials && item.shouldBlur) ? 'none' : 'auto',
                      transition: 'filter 0.3s ease',
                    }}
                  >
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Schedule with week navigation ─────────────────────────────────── */}
        {dashboardData.schedule && (
          <Box sx={{ width: '365px', mt: 1, mb: 1 }}>

            {/* Week navigation header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.75,
                px: 0.5,
              }}
            >
              {/* Prev week button */}
              <IconButton
                onClick={() => setWeekOffset((o) => o - 1)}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                  color: '#fff',
                  width: 32,
                  height: 32,
                  '&:hover': { background: 'linear-gradient(135deg, #db2777, #9333ea)', transform: 'scale(1.08)' },
                  transition: 'transform 0.15s',
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              {/* Week label */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: 0.3,
                  }}
                >
                  {isCurrentWeek
                    ? 'THIS WEEK'
                    : weekOffset > 0
                    ? `+${weekOffset} week${weekOffset > 1 ? 's' : ''}`
                    : `${Math.abs(weekOffset)} week${Math.abs(weekOffset) > 1 ? 's' : ''} ago`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                  {toDisplayDate(viewedMonday)} – {toDisplayDate(viewedSunday)}
                </Typography>
              </Box>

              {/* Next week button */}
              <IconButton
                onClick={() => setWeekOffset((o) => o + 1)}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                  color: '#fff',
                  width: 32,
                  height: 32,
                  '&:hover': { background: 'linear-gradient(135deg, #db2777, #9333ea)', transform: 'scale(1.08)' },
                  transition: 'transform 0.15s',
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Schedule table */}
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.11)',
                borderLeft: '3px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(180deg, #ec4899, #a855f7)`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                backgroundColor: '#fff',
              }}
            >
              {isLoadingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: '#a855f7' }} />
                </Box>
              ) : scheduleError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <Typography variant="caption" sx={{ color: '#b91c1c' }}>{scheduleError}</Typography>
                </Box>
              ) : !scheduleReleased ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, px: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center' }}>
                    🔒 This week's schedule hasn't been released yet.
                  </Typography>
                </Box>
              ) : (
                <Table>
                  <TableBody>
                    {orderedDays.map((day) => {
                      const hotel = activeSchedule?.[day] ?? 'OFF';
                      // Only highlight "today" when viewing the current week
                      const isToday = isCurrentWeek && day.toUpperCase() === todayFormattedDay;

                      return (
                        <TableRow key={day}>
                          <TableCell colSpan={2} sx={{ borderBottom: 'none', p: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                my: 1,
                                backgroundColor: '#fff',
                                boxShadow: isToday
                                  ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.08)'
                                  : 'none',
                                transform: isToday ? 'translateY(-2px)' : 'none',
                                transition: 'all 0.3s ease',
                                '&:hover': { transform: isToday ? 'translateY(-4px)' : 'none' },
                              }}
                            >
                              <Typography sx={{ fontWeight: 'bold', width: '40%' }}>{day}</Typography>
                              <Typography sx={{ flex: 1, textAlign: 'center' }}>{hotel}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        )}

      </Box>
    </Box>
  );
};